import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

interface User {
  id: number;
  email: string;
  username: string;
  role: string;
}

interface RegisterResult {
  success: boolean;
  email?: string;
  error?: string;
  emailVerificationSkipped?: boolean;
}

interface VerifyResult {
  success: boolean;
  error?: string;
  expired?: boolean;
  locked?: boolean;
  remainingAttempts?: number;
}

interface ResendResult {
  success: boolean;
  error?: string;
  remainingResends?: number;
  waitTime?: number;
  expired?: boolean;
  maxResends?: boolean;
}

interface ForgotPasswordResult {
  success: boolean;
  message?: string;
  error?: string;
}

interface VerifyResetTokenResult {
  valid: boolean;
  error?: string;
}

interface ResetPasswordResult {
  success: boolean;
  message?: string;
  error?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string, username?: string, referral_code?: string, first_name?: string, last_name?: string) => Promise<RegisterResult>;
  verifyEmail: (email: string, code: string) => Promise<VerifyResult>;
  resendCode: (email: string) => Promise<ResendResult>;
  forgotPassword: (email: string) => Promise<ForgotPasswordResult>;
  verifyResetToken: (token: string) => Promise<VerifyResetTokenResult>;
  resetPassword: (token: string, newPassword: string) => Promise<ResetPasswordResult>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const API_BASE = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    // Set up axios interceptor to handle auto-refreshed tokens
    const interceptor = axios.interceptors.response.use((response) => {
      const refreshedToken = response.headers['x-refreshed-token'];
      if (refreshedToken) {
        console.log('🔄 Token auto-refreshed');
        localStorage.setItem('auth_token', refreshedToken);
        setToken(refreshedToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${refreshedToken}`;
      }
      return response;
    });

    const storedToken = localStorage.getItem('auth_token');
    if (storedToken) {
      setToken(storedToken);
      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      // Verify token and get user info
      fetchUserInfo(storedToken);
    } else {
      setLoading(false);
    }

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const fetchUserInfo = async (authToken: string) => {
    try {
      const response = await axios.get(`${API_BASE}/api/auth/me`, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });
      setUser(response.data.user);
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      // Token is invalid, clear it
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      console.log('🔐 Login attempt:', { 
        email, 
        password: password.substring(0, 3) + '***', 
        API_BASE,
        fullUrl: `${API_BASE}/api/auth/login`
      });
      
      // First test basic connectivity
      try {
        const healthCheck = await axios.get(`${API_BASE}/health`);
        console.log('✅ Health check successful:', healthCheck.status);
      } catch (healthError) {
        console.error('❌ Health check failed:', healthError);
      }
      
      const response = await axios.post(`${API_BASE}/api/auth/login`, {
        email,
        password
      }, {
        timeout: 10000, // 10 second timeout
        withCredentials: true, // Required for cookies to be set
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Login response:', response.status, response.data);
      
      const { token: authToken, user: userData } = response.data;
      
      setToken(authToken);
      setUser(userData);
      localStorage.setItem('auth_token', authToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      
      return true;
    } catch (error: any) {
      console.error('❌ Login failed:', error);
      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        console.error('Response headers:', error.response.headers);
      } else if (error.request) {
        console.error('Request was made but no response received:', error.request);
        console.error('Request config:', error.config);
        console.error('Request readyState:', error.request.readyState);
        console.error('Request status:', error.request.status);
      } else {
        console.error('Error setting up request:', error.message);
      }
      console.error('Error code:', error.code);
      console.error('Error config:', error.config);
      return false;
    }
  };

  const register = async (email: string, password: string, username?: string, referral_code?: string, first_name?: string, last_name?: string): Promise<RegisterResult> => {
    try {
      const response = await axios.post(`${API_BASE}/api/auth/register`, {
        email,
        password,
        username,
        referral_code,
        first_name,
        last_name
      });

      // Check if email verification was skipped (bypass enabled)
      if (response.data.emailVerificationSkipped) {
        // User was created directly - set auth state
        const { token: authToken, user: userData } = response.data;
        setToken(authToken);
        setUser(userData);
        localStorage.setItem('auth_token', authToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;

        return {
          success: true,
          emailVerificationSkipped: true
        };
      }

      // Normal flow - returns email for verification step
      return {
        success: true,
        email: response.data.email
      };
    } catch (error: any) {
      console.error('Registration failed:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed. Please try again.'
      };
    }
  };

  const verifyEmail = async (email: string, code: string): Promise<VerifyResult> => {
    try {
      const response = await axios.post(`${API_BASE}/api/auth/verify-email`, {
        email,
        code
      });

      // Verification successful - set user and token (auto-login)
      const { token: authToken, user: userData } = response.data;
      setToken(authToken);
      setUser(userData);
      localStorage.setItem('auth_token', authToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;

      return { success: true };
    } catch (error: any) {
      console.error('Email verification failed:', error);
      const data = error.response?.data || {};
      return {
        success: false,
        error: data.error || 'Verification failed. Please try again.',
        expired: data.expired,
        locked: data.locked,
        remainingAttempts: data.remainingAttempts
      };
    }
  };

  const resendCode = async (email: string): Promise<ResendResult> => {
    try {
      const response = await axios.post(`${API_BASE}/api/auth/resend-code`, {
        email
      });

      return {
        success: true,
        remainingResends: response.data.remainingResends
      };
    } catch (error: any) {
      console.error('Resend code failed:', error);
      const data = error.response?.data || {};
      return {
        success: false,
        error: data.error || 'Failed to resend code. Please try again.',
        waitTime: data.waitTime,
        expired: data.expired,
        maxResends: data.maxResends
      };
    }
  };

  const forgotPassword = async (email: string): Promise<ForgotPasswordResult> => {
    try {
      const response = await axios.post(`${API_BASE}/api/auth/forgot-password`, { email });
      return { success: true, message: response.data.message };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Request failed. Please try again.'
      };
    }
  };

  const verifyResetToken = async (token: string): Promise<VerifyResetTokenResult> => {
    try {
      const response = await axios.get(`${API_BASE}/api/auth/verify-reset-token/${token}`);
      return { valid: response.data.valid };
    } catch (error: any) {
      return {
        valid: false,
        error: error.response?.data?.error || 'Invalid or expired reset link'
      };
    }
  };

  const resetPassword = async (token: string, newPassword: string): Promise<ResetPasswordResult> => {
    try {
      const response = await axios.post(`${API_BASE}/api/auth/reset-password`, {
        token,
        newPassword
      });
      return { success: true, message: response.data.message };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Password reset failed. Please try again.'
      };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    register,
    verifyEmail,
    resendCode,
    forgotPassword,
    verifyResetToken,
    resetPassword,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};